import type {
  CreditSpreadBacktest,
  GetCreditSpreadChainResponse,
  ListDailyBarsResponse,
  ListEarningsResponse,
  OptionRight,
  StartCreditSpreadBacktestRequest,
  StartCreditSpreadBacktestResponse,
} from '@fleece/models';
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { useApi } from '@/hooks/use-connection';
import { isBacktestLost } from '@/lib/backtest-status';
import { config } from '@/lib/config';

export function useChain(symbol: string, right: OptionRight): UseQueryResult<GetCreditSpreadChainResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['credit-spreads', 'chain', symbol, right],
    queryFn: () => api.getCreditSpreadChain({ symbol, right }),
    enabled: symbol.length > 0,
    // Quotes move, but a chain is a few Alpaca pages: refresh on demand, not on a timer.
    staleTime: 60_000,
  });
}

export function useDailyBars(symbol: string, from: string, to: string): UseQueryResult<ListDailyBarsResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['credit-spreads', 'daily-bars', symbol, from, to],
    queryFn: () => api.listDailyBars({ symbol, from, to }),
    enabled: symbol.length > 0,
    staleTime: 5 * 60_000,
  });
}

/** A symbol's earnings reports. The calendar is a file on the service, so this changes only when it is edited. */
export function useEarnings(symbol: string): UseQueryResult<ListEarningsResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['credit-spreads', 'earnings', symbol],
    queryFn: () => api.listEarnings({ symbol }),
    enabled: symbol.length > 0,
    staleTime: Infinity,
  });
}

export function useStartBacktest(): UseMutationResult<StartCreditSpreadBacktestResponse, Error, StartCreditSpreadBacktestRequest> {
  const api = useApi();
  return useMutation({ mutationFn: (request: StartCreditSpreadBacktestRequest) => api.startCreditSpreadBacktest(request) });
}

/**
 * Polls a backtest while it runs and stops once it has an outcome, or once the service says
 * it has none. Any other failure keeps polling: an unreachable service may come back, and
 * the backtest may still be running in it.
 */
export function useBacktest(backtestId: string | undefined): UseQueryResult<CreditSpreadBacktest> {
  const api = useApi();
  return useQuery({
    queryKey: ['credit-spreads', 'backtest', backtestId],
    queryFn: async () => (await api.getCreditSpreadBacktest({ backtestId: backtestId ?? '' })).backtest,
    enabled: backtestId !== undefined,
    refetchInterval: (query) => {
      if (isBacktestLost(query.state.error)) {
        return false;
      }
      return query.state.data?.status === 'running' || query.state.data === undefined ? config.backtestPollMs : false;
    },
  });
}
